import React, { useState, useEffect, useRef } from 'react';
import { Edit, Trash2, Eye } from 'lucide-react';
import '../css/merchent.css';
import axiosInstance from "../../../axiosConfig";
import CompactMerchantManagementPage from '../../App/Pages/Merchant/MerchantList';

export default function BankAccountManagement({ entityUuid, entityId, entityName, entityType, entityCode }) {
  const [accountAssociation, setAccountAssociation] = useState('main');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  
  // Bank metadata state
  const [banks, setBanks] = useState([]);
  const [bankBranches, setBankBranches] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);

  // Organization data from props with fallbacks
  const organizationData = {
    name: entityName || 'Merchant Name',
    registration: entityCode || 'N/A',
    type: entityType || 'N/A',
    id: entityId || '',
    uuid: entityUuid || ''
  };

  // Fetch bank metadata
  const fetchBankMetadata = async () => {
    setMetaLoading(true);
    try {
      const response = await axiosInstance.get(
        '/internal-api/v1/client-web/entity-panel/registration/bank-account-meta'
      );
      
      console.log('Bank Metadata Response:', response.data);
      
      if (response.data && response.data.result) {
        setBanks(response.data.result.banks || []);
        setBankBranches(response.data.result.bankBranches || []);
        setAccountTypes(response.data.result.accountTypes || []);
      }
    } catch (error) {
      console.error('Error fetching bank metadata:', error);
    } finally {
      setMetaLoading(false);
    }
  };

  // Fetch branches
  const fetchBranches = async () => {
    if (!entityUuid) {
      console.warn('Entity UUID not available for fetching branches');
      return;
    }
    
    setBranchesLoading(true);
    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/entity-panel/config/${entityUuid}/view-info`
      );
      
      console.log('View-info API Response:', response.data);
      
      if (response.data && response.data.result && response.data.result.entityBranches) {
        const branches = response.data.result.entityBranches.map(branch => ({
          uuid: branch.uuid,
          name: branch.name,
          id: branch.id,
          entityId: response.data.result.entity.id
        }));
        setAvailableBranches(branches);
        console.log('Loaded branches:', branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setBranchesLoading(false);
    }
  };

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    if (!entityUuid) return;
    
    setAccountsLoading(true);
    try {
      const response = await axiosInstance.get(
        '/internal-api/v1/client-web/entity-panel/registration/entity-bank-accounts?start=0&length=100'
      );

      console.log('Bank Accounts API Response:', response.data);

      if (response.data && response.data.data) {
        const filteredAccounts = response.data.data.filter(acc => 
          acc.entityUuid === entityUuid
        );
        setBankAccounts(filteredAccounts);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setBankAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    fetchBankMetadata();
    fetchBranches();
    fetchBankAccounts();
    console.log('Using Entity UUID:', entityUuid);
  }, [entityUuid]);

  const handleAssociationChange = (association) => {
    setAccountAssociation(association);
  };

  return (
    <>
      <link rel="stylesheet" href="styles.css" />
      <div className="merchant-container">
        {/* Show info message if no entity data is available */}
        {!entityId && !entityUuid ? (
          <div className="p-6">
            <div className="p-6 text-center rounded bg-blue-50">
              <h3 className="mb-3 text-base font-medium text-gray-700">Bank Account Management</h3>
              <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable bank account management.</p>
              <p className="mt-3 text-xs text-gray-600">After saving the merchant, you will be able to manage bank accounts including:</p>
              <p className="mt-2 text-xs text-gray-600">
                • Main merchant bank accounts • Branch-specific bank accounts • Account verification documents
              </p>
            </div>
          </div>
        ) : (
          <>
            {accountAssociation === 'main' ? (
              <MainMerchantView 
                onAssociationChange={handleAssociationChange}
                entityUuid={entityUuid}
                organizationData={organizationData}
                bankAccounts={bankAccounts}
                accountsLoading={accountsLoading}
                refreshAccounts={fetchBankAccounts}
                banks={banks}
                bankBranches={bankBranches}
                accountTypes={accountTypes}
                metaLoading={metaLoading}
                availableBranches={availableBranches}
              />
            ) : (
              <BranchSpecificView 
                selectedBranch={selectedBranch}
                onAssociationChange={handleAssociationChange}
                onBackToMerchant={() => setAccountAssociation('main')}
                availableBranches={availableBranches}
                branchesLoading={branchesLoading}
                setSelectedBranch={setSelectedBranch}
                entityUuid={entityUuid}
                organizationData={organizationData}
                bankAccounts={bankAccounts}
                accountsLoading={accountsLoading}
                refreshAccounts={fetchBankAccounts}
                banks={banks}
                bankBranches={bankBranches}
                accountTypes={accountTypes}
                metaLoading={metaLoading}
              />
            )}
          </>
        )}

        {showBrowseModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 className="modal-title">File Browser</h3>
              <p className="modal-text">Select a file from your device</p>
              <div className="justify-end">
                <button 
                  className="modal-close"
                  onClick={() => setShowBrowseModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function MainMerchantView({ onAssociationChange, entityUuid, organizationData, bankAccounts, accountsLoading, refreshAccounts, banks, bankBranches, accountTypes, metaLoading, availableBranches }) {
  const [bankId, setBankId] = useState('');
  const [bankBranchId, setBankBranchId] = useState('');
  const [accountTypeId, setAccountTypeId] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [holderName, setHolderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Filter bank branches based on selected bank
  const filteredBankBranches = bankId 
    ? bankBranches.filter(branch => branch.bankId === parseInt(bankId))
    : [];

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFiles([...files]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles([...files]);
    }
  };

  const handleSave = async () => {
    if (!entityUuid) {
      alert('Entity UUID is required. Please complete the basic information first.');
      return;
    }

    let hasError = false;
    let message = '';

    if (!bankId) { hasError = true; message = 'Please select a bank'; }
    else if (!bankBranchId) { hasError = true; message = 'Please select a bank branch'; }
    else if (!accountTypeId) { hasError = true; message = 'Please select an account type'; }
    else if (!accountNo) { hasError = true; message = 'Please enter account number'; }
    else if (!holderName) { hasError = true; message = 'Please enter holder name'; }

    if (hasError) {
      alert(message);
      return;
    }

    if (selectedFiles.length === 0) {
      alert('Please upload at least one support document');
      return;
    }

    const formdata = new FormData();
    
    formdata.append('tenantType', 'ENTITY');
    formdata.append('entityUuid', entityUuid);
    formdata.append('entityBranchUuid', '');
    formdata.append('bankId', bankId);
    formdata.append('bankBranchId', bankBranchId);
    formdata.append('accountTypeId', accountTypeId);
    formdata.append('accountNo', accountNo);
    formdata.append('holderName', holderName);

    const docIndex = 0;
    formdata.append(`supportDocuments[${docIndex}][entityUuid]`, entityUuid);
    formdata.append(`supportDocuments[${docIndex}][entityBranchUuid]`, '');
    formdata.append(`supportDocuments[${docIndex}][tenantType]`, 'ENTITY');
    formdata.append(`supportDocuments[${docIndex}][guidelineId]`, '3');
    formdata.append(`supportDocuments[${docIndex}][note]`, '');

    selectedFiles.forEach((file, fileIndex) => {
      formdata.append(`supportDocuments[${docIndex}][files][${fileIndex}]`, file);
    });

    console.log('Submitting bank account for main merchant...');
    console.log('Entity UUID:', entityUuid);
    console.log('Bank ID:', bankId);
    console.log('Bank Branch ID:', bankBranchId);
    console.log('Account Type ID:', accountTypeId);

    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/entity-panel/registration/new-bank-account',
        formdata,
      );

      console.log('Bank account creation response:', response.data);

      if (response.data && response.data.result) {
        alert('Bank account saved successfully!');
        setBankId(''); setBankBranchId(''); setAccountTypeId(''); setAccountNo(''); setHolderName(''); setSelectedFiles([]);
        refreshAccounts();
      }
    } catch (error) {
      console.error("Error saving bank account:", error);
      alert('Error saving bank account. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="merchant-header">
        <h3 className="merchant-title">{organizationData.name}</h3>
        <div className="merchant-subtitle">
          Registration: {organizationData.registration}
        </div>
        <div className="merchant-subtitle">
          Type: {organizationData.type}
        </div>
        {/* {merchantInfo.uuid && (
          <div className="text-xs text-gray-500 merchant-subtitle">
            UUID: {merchantInfo.uuid} | Entity ID: {merchantInfo.id}
          </div>
        )} */}
      </div>

      <div className="form-card">
        <h2 className="section-title">Add New Bank Account</h2>
        
        <div className="mb-6">
          <label className="form-label required">Account Association</label>
          <div className="flex-gap-4">
            <label className="flex items-center">
              <input type="radio" name="accountAssociation" checked={true} onChange={() => onAssociationChange('main')} className="radio-input" />
              <span className="badge-main-merchant">Main Merchant</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name="accountAssociation" onChange={() => onAssociationChange('branch')} className="radio-input" />
              <span className="text-xs">Branch Specific</span>
            </label>
          </div>
        </div>
        
        <div className="grid-cols-2">
          <div>
            <label className="form-label required">Bank</label>
            <select className="form-select" value={bankId} onChange={(e) => { setBankId(e.target.value); setBankBranchId(''); }}>
              <option value="">{metaLoading ? 'Loading...' : 'Select bank'}</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label required">Branch</label>
            <select className="form-select" value={bankBranchId} onChange={(e) => setBankBranchId(e.target.value)} disabled={!bankId}>
              <option value="">{!bankId ? 'Select bank first' : 'Select branch'}</option>
              {filteredBankBranches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label required">Account Type</label>
            <select className="form-select" value={accountTypeId} onChange={(e) => setAccountTypeId(e.target.value)}>
              <option value="">{metaLoading ? 'Loading...' : 'Select account type'}</option>
              {accountTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label required">Account Number</label>
            <input type="text" placeholder="Enter account number" className="form-input" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
          </div>
          <div>
            <label className="form-label required">Account Holder Name</label>
            <input type="text" placeholder="Enter account holder name" className="form-input" value={holderName} onChange={(e) => setHolderName(e.target.value)} />
          </div>
        </div>
        
        <div className="mt-6">
          <label className="form-label required">Upload Account Proof</label>
          <div className="file-upload-area" onDrop={handleDrop} onDragOver={handleDragOver}>
            <p className="upload-text">Drag and drop files here</p>
            <p className="upload-or">or</p>
            <button className="btn-browse" onClick={() => fileInputRef.current.click()}>Browse Files</button>
            <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileSelect} />
            {selectedFiles.length > 0 && (
              <div className="mt-2 text-xs text-green-600">
                {selectedFiles.length} file(s) selected
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-6 text-xs text-gray-500"><p>* Required fields</p></div>
        
        <div className="flex justify-end mt-4 flex-gap-2">
          <button className="btn-secondary">Cancel</button>
          <button className="btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
      
      <div className="form-card">
        <h2 className="section-title">Current Accounts</h2>
        
        {accountsLoading ? (
          <p className="py-8 text-xs text-center text-gray-500">Loading accounts...</p>
        ) : bankAccounts.length === 0 ? (
          <p className="py-8 text-xs text-center text-gray-500">No bank accounts found.</p>
        ) : (
          <table className="accounts-table">
            <thead className="table-header">
              <tr>
                <th>Bank</th>
                <th>Branch</th>
                <th>Account Number</th>
                <th>Association</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {bankAccounts.map(acc => (
                <tr key={acc.id}>
                  <td className="table-cell">{acc.bankName}</td>
                  <td className="table-cell">{acc.branchName}</td>
                  <td className="table-cell">•••• •••• {acc.accountNumber.slice(-4)}</td>
                  <td className="table-cell">
                    {acc.entityBranchUuid ? (
                      availableBranches.find(b => b.uuid === acc.entityBranchUuid)?.name || 'Branch'
                    ) : (
                      'Main Merchant'
                    )}
                  </td>
                  <td className="table-cell">
                    {/* <button className="p-1 action-btn action-btn-blue"><Eye size={16} /></button>
                    <button className="p-1 action-btn action-btn-yellow"><Edit size={16} /></button> */}
                    <button className="p-1 action-btn action-btn-red"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
      </div>
        <div className="flex justify-end mt-4 flex-gap-2">
          {/* <button className="btn-secondary">Cancel</button> */}
          <button className="btn-primary" onClick={() => {
            // Clear uploaded documents from localStorage
            if (entityUuid) {
              localStorage.removeItem(`uploadedDocuments_${entityUuid}`);
            }
            window.location.href = '/CompactMerchantManagementPage';
          }}>Finish Registration</button>
        </div>
    </div>
  );
}

function BranchSpecificView({ selectedBranch, onAssociationChange, onBackToMerchant, availableBranches, branchesLoading, setSelectedBranch, entityUuid, organizationData, bankAccounts, accountsLoading, refreshAccounts, banks, bankBranches, accountTypes, metaLoading }) {
  const [isBranchSpecific, setIsBranchSpecific] = useState(true);
  const [useMerchantAccount, setUseMerchantAccount] = useState('no');
  const [bankId, setBankId] = useState('');
  const [bankBranchId, setBankBranchId] = useState('');
  const [accountTypeId, setAccountTypeId] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [holderName, setHolderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [selectedMerchantAccountIds, setSelectedMerchantAccountIds] = useState([]);
  const [reuseSelectedFiles, setReuseSelectedFiles] = useState([]);
  const reuseFileInputRef = useRef(null);

  // Filter bank branches based on selected bank
  const filteredBankBranches = bankId 
    ? bankBranches.filter(branch => branch.bankId === parseInt(bankId))
    : [];

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFiles([...files]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles([...files]);
    }
  };

  const handleReuseFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setReuseSelectedFiles([...files]);
    }
  };

  const mainMerchantAccounts = bankAccounts.filter(acc => !acc.entityBranchUuid);

  const toggleMerchantAccountSelection = (accountId) => {
    setSelectedMerchantAccountIds((prev) => {
      if (prev.includes(accountId)) return prev.filter(id => id !== accountId);
      return [...prev, accountId];
    });
  };

  const handleSaveSelectedMerchantAccountsToBranch = async () => {
    if (!entityUuid) {
      alert('Entity UUID is missing. Please ensure entity is loaded.');
      return;
    }

    if (!isBranchSpecific) {
      alert('Please select Branch Specific association to link accounts to a branch.');
      return;
    }

    if (!selectedBranch) {
      alert('Please select a branch first.');
      return;
    }

    if (selectedMerchantAccountIds.length === 0) {
      alert('Please select at least one merchant bank account to use for this branch.');
      return;
    }

    if (reuseSelectedFiles.length === 0) {
      alert('Please upload at least one support document to assign selected accounts to the branch.');
      return;
    }

    const selectedAccounts = mainMerchantAccounts.filter(acc => selectedMerchantAccountIds.includes(acc.id));
    const branchName = availableBranches.find(b => b.uuid === selectedBranch)?.name || 'Branch';

    const confirmMessage = `Save ${selectedAccounts.length} merchant bank account(s) for branch "${branchName}"?`;
    if (!window.confirm(confirmMessage)) return;

    let successCount = 0;
    let failCount = 0;

    const existingBranchAccounts = bankAccounts.filter(acc => acc.entityBranchUuid === selectedBranch);
    const alreadyExistsForBranch = (candidate) => {
      const candidateAccountNo = (candidate.accountNo || candidate.accountNumber || '').toString();
      const candidateBankId = String(candidate.bankId || candidate.bank_id || '');
      const candidateBankBranchId = String(candidate.bankBranchId || candidate.branchId || candidate.bank_branch_id || '');
      return existingBranchAccounts.some(acc => {
        const accNo = (acc.accountNo || acc.accountNumber || '').toString();
        const accBankId = String(acc.bankId || acc.bank_id || '');
        const accBranchId = String(acc.bankBranchId || acc.branchId || acc.bank_branch_id || '');
        return accNo === candidateAccountNo && accBankId === candidateBankId && accBranchId === candidateBankBranchId;
      });
    };

    const resolveAccountTypeId = (account) => {
      if (account.accountTypeId) return account.accountTypeId;
      const typeName = (account.accountType || account.accountTypeName || '').toString().trim().toLowerCase();
      if (!typeName) return '';
      const matched = accountTypes.find(t => (t.name || '').toString().trim().toLowerCase() === typeName);
      return matched ? matched.id : '';
    };

    for (const account of selectedAccounts) {
      if (alreadyExistsForBranch(account)) {
        console.log('Skipping (already exists for branch):', account);
        continue;
      }

      const normalizedAccountNo = account.accountNo || account.accountNumber || '';
      const normalizedHolderName = account.holderName || account.accountHolderName || account.holder || '';
      const normalizedBankId = account.bankId || account.bank_id || '';
      // API response uses `branchId` for the bank branch id
      const normalizedBankBranchId = account.bankBranchId || account.branchId || account.bank_branch_id || '';
      // API response uses `accountType` as name; resolve to id using meta
      const normalizedAccountTypeId = resolveAccountTypeId(account) || account.account_type_id || '';

      const missing = [];
      if (!normalizedBankId) missing.push('bankId');
      if (!normalizedBankBranchId) missing.push('bankBranchId/branchId');
      if (!normalizedAccountTypeId) missing.push('accountTypeId (match accountTypes)');
      if (!normalizedAccountNo) missing.push('accountNo/accountNumber');
      if (!normalizedHolderName) missing.push('holderName');

      if (missing.length > 0) {
        console.warn('Skipping account due to missing fields:', { missing, account });
        failCount += 1;
        continue;
      }

      const formdata = new FormData();
      formdata.append('tenantType', 'ENTITY_BRANCH');
      formdata.append('entityUuid', entityUuid);
      formdata.append('entityBranchUuid', selectedBranch);
      formdata.append('bankId', String(normalizedBankId));
      formdata.append('bankBranchId', String(normalizedBankBranchId));
      formdata.append('accountTypeId', String(normalizedAccountTypeId));
      formdata.append('accountNo', String(normalizedAccountNo));
      formdata.append('holderName', String(normalizedHolderName));

      // Match manual save behavior: always include at least one support document
      const docIndex = 0;
      formdata.append(`supportDocuments[${docIndex}][entityUuid]`, entityUuid);
      formdata.append(`supportDocuments[${docIndex}][entityBranchUuid]`, selectedBranch);
      formdata.append(`supportDocuments[${docIndex}][tenantType]`, 'ENTITY_BRANCH');
      formdata.append(`supportDocuments[${docIndex}][guidelineId]`, '3');
      formdata.append(`supportDocuments[${docIndex}][note]`, '');

      reuseSelectedFiles.forEach((file, fileIndex) => {
        formdata.append(`supportDocuments[${docIndex}][files][${fileIndex}]`, file);
      });

      console.log('Submitting selected merchant account as branch account...');
      console.log('Entity UUID:', entityUuid);
      console.log('Entity Branch UUID:', selectedBranch);
      console.log('Tenant Type:', 'ENTITY_BRANCH');
      console.log('=== FormData Contents (Save Selected) ===');
      for (let pair of formdata.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      console.log('========================================');

      try {
        const response = await axiosInstance.post(
          '/internal-api/v1/client-web/entity-panel/registration/new-bank-account',
          formdata,
        );

        if (response.data && response.data.result) {
          successCount += 1;
        } else {
          failCount += 1;
        }
      } catch (error) {
        console.error('Error saving selected merchant account to branch:', {
          message: error?.message,
          status: error?.response?.status,
          data: error?.response?.data,
        });
        failCount += 1;
      }
    }

    await refreshAccounts();
    alert(`Done. Success: ${successCount}, Failed: ${failCount}.`);
  };

  const handleSave = async () => {
    if (!entityUuid) {
      alert('Entity UUID is missing. Please ensure entity is loaded.');
      return;
    }

    // Determine the actual branch UUID to use
    const branchUuidToUse = isBranchSpecific ? selectedBranch : '';

    // If branch-specific is selected, ensure a branch is chosen
    if (isBranchSpecific && !selectedBranch) {
      alert('Please select a branch before adding bank account.');
      return;
    }

    console.log('Is Branch Specific:', isBranchSpecific);
    console.log('Selected Branch UUID before save:', selectedBranch);
    console.log('Branch UUID to use:', branchUuidToUse);
    console.log('Available branches:', availableBranches);

    let hasError = false;
    let message = '';

    if (!bankId) { hasError = true; message = 'Please select a bank'; }
    else if (!bankBranchId) { hasError = true; message = 'Please select a bank branch'; }
    else if (!accountTypeId) { hasError = true; message = 'Please select an account type'; }
    else if (!accountNo) { hasError = true; message = 'Please enter account number'; }
    else if (!holderName) { hasError = true; message = 'Please enter holder name'; }

    if (hasError) {
      alert(message);
      return;
    }

    if (selectedFiles.length === 0) {
      alert('Please upload at least one support document');
      return;
    }

    const formdata = new FormData();
    
    // Set tenantType based on whether it's branch-specific or main merchant
    const tenantType = isBranchSpecific && branchUuidToUse ? 'ENTITY_BRANCH' : 'ENTITY';
    
    formdata.append('tenantType', tenantType);
    formdata.append('entityUuid', entityUuid);
    formdata.append('entityBranchUuid', branchUuidToUse);
    formdata.append('bankId', bankId);
    formdata.append('bankBranchId', bankBranchId);
    formdata.append('accountTypeId', accountTypeId);
    formdata.append('accountNo', accountNo);
    formdata.append('holderName', holderName);

    const docIndex = 0;
    formdata.append(`supportDocuments[${docIndex}][entityUuid]`, entityUuid);
    formdata.append(`supportDocuments[${docIndex}][entityBranchUuid]`, branchUuidToUse);
    formdata.append(`supportDocuments[${docIndex}][tenantType]`, tenantType);
    formdata.append(`supportDocuments[${docIndex}][guidelineId]`, '3');
    formdata.append(`supportDocuments[${docIndex}][note]`, '');

    selectedFiles.forEach((file, fileIndex) => {
      formdata.append(`supportDocuments[${docIndex}][files][${fileIndex}]`, file);
    });

    // Show confirmation with details
    const accountTypeText = isBranchSpecific ? `Branch: ${availableBranches.find(b => b.uuid === selectedBranch)?.name}` : 'Main Merchant';
    const confirmMessage = `Are you sure you want to save this bank account?\n\n` +
      `Account Type: ${accountTypeText}\n` +
      `Branch UUID: ${branchUuidToUse || '(None - Main Merchant)'}\n` +
      `Bank: ${banks.find(b => b.id === parseInt(bankId))?.name}\n` +
      `Account Number: ${accountNo}`;
    
    if (!window.confirm(confirmMessage)) {
      console.log('Save cancelled by user');
      return;
    }

    console.log('Submitting bank account...');
    console.log('Account Type:', isBranchSpecific ? 'Branch Specific' : 'Main Merchant');
    console.log('Tenant Type:', tenantType);
    console.log('Entity UUID:', entityUuid);
    console.log('Entity Branch UUID:', branchUuidToUse);
    console.log('Bank ID:', bankId);
    console.log('Bank Branch ID:', bankBranchId);
    console.log('Account Type ID:', accountTypeId);

    // Log all FormData entries
    console.log('=== FormData Contents ===');
    for (let pair of formdata.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }
    console.log('========================');

    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/entity-panel/registration/new-bank-account',
        formdata,
      );

      console.log('Bank account creation response:', response.data);

      if (response.data && response.data.result) {
        alert('Bank account saved successfully!');
        setBankId(''); setBankBranchId(''); setAccountTypeId(''); setAccountNo(''); setHolderName(''); setSelectedFiles([]);
        refreshAccounts();
      }
    } catch (error) {
      console.error("Error saving bank account:", error);
      alert('Error saving bank account. Please try again.');
    }
  };

  const branchAccounts = bankAccounts.filter(acc => acc.entityBranchUuid === selectedBranch);

  return (
    <div className="space-y-6">
      <div className="merchant-header">
        <h3 className="merchant-title">{organizationData.name}</h3>
        <div className="merchant-subtitle">
          Registration: {organizationData.registration}
        </div>
        <div className="merchant-subtitle">
          Type: {organizationData.type}
        </div>
        {/* {merchantInfo.uuid && (
          <div className="text-xs text-gray-500 merchant-subtitle">
            UUID: {merchantInfo.uuid} | Entity ID: {merchantInfo.id}
          </div>
        )} */}
      </div>

      <div className="merchant-info-card">
            <div className="mb-6">
              <label className="mb-2 form-label required">Account Association</label>
              <div className="flex-gap-4">
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    name="accountAssociation" 
                    checked={!isBranchSpecific}
                    onChange={() => {
                      setIsBranchSpecific(false);
                      setSelectedBranch('');
                      onAssociationChange('main');
                    }} 
                    className="radio-input" 
                  />
                  <span className="text-xs">Main Merchant</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="radio" 
                    name="accountAssociation" 
                    checked={isBranchSpecific}
                    onChange={() => setIsBranchSpecific(true)}
                    className="radio-input" 
                  />
                  <span className="badge-main-merchant">Branch Specific</span>
                </label>
              </div>
              
              {isBranchSpecific && (
                <div className="mt-4">
                  <label className="form-label required">Select Branch:</label>
                  <select 
                    className="form-select"
                    value={selectedBranch}
                    onChange={(e) => {
                      console.log('Branch selected:', e.target.value);
                      setSelectedBranch(e.target.value);
                    }}
                    disabled={branchesLoading}
                    style={{ borderColor: !selectedBranch ? '#f87171' : '' }}
                  >
                    <option value="">{branchesLoading ? 'Loading branches...' : 'Please select a branch'}</option>
                    {availableBranches
                      .filter(branch => branch.entityId === organizationData.id)
                      .map(branch => (
                        <option key={branch.uuid} value={branch.uuid}>{branch.name}</option>
                      ))}
                  </select>
                  {!selectedBranch && (
                    <p className="mt-1 text-xs text-red-500">⚠ You must select a branch to add accounts</p>
                  )}
                  {selectedBranch && (
                    <p className="mt-1 text-xs text-green-600">✓ Selected: {availableBranches.find(b => b.uuid === selectedBranch)?.name}</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4">
              <span className="mr-8 text-xs font-medium">Use merchant bank account:</span>
              <label className="inline-flex items-center mr-4">
                <input type="radio" name="useMerchantAccount" value="yes" className="inline-radio" checked={useMerchantAccount === 'yes'} onChange={() => setUseMerchantAccount('yes')} />
                <span className="text-xs">Yes</span>
              </label>
              <label className="inline-flex items-center px-3 py-1 bg-blue-100 rounded">
                <input type="radio" name="useMerchantAccount" value="no" className="inline-radio" checked={useMerchantAccount === 'no'} onChange={() => setUseMerchantAccount('no')} />
                <span className="text-xs text-blue-800">No</span>
              </label>
            </div>
          </div>

          {useMerchantAccount === 'yes' && (
            <div className="form-card">
              <h2 className="section-title">
                Use Merchant Bank Accounts for {availableBranches.find(b => b.uuid === selectedBranch)?.name || 'Branch'}
              </h2>

              {!isBranchSpecific ? (
                <div className="py-8 text-center">
                  <p className="mb-2 text-sm text-gray-500">⚠ Branch Specific not selected</p>
                  <p className="text-xs text-gray-400">Please select “Branch Specific” in Account Association.</p>
                </div>
              ) : !selectedBranch ? (
                <div className="py-8 text-center">
                  <p className="mb-2 text-sm text-gray-500">⚠ Branch not selected</p>
                  <p className="text-xs text-gray-400">Please select a branch to assign merchant bank accounts.</p>
                </div>
              ) : accountsLoading ? (
                <p className="py-8 text-xs text-center text-gray-500">Loading accounts...</p>
              ) : mainMerchantAccounts.length === 0 ? (
                <p className="py-8 text-xs text-center text-gray-500">No main merchant bank accounts found to reuse.</p>
              ) : (
                <>
                  <div className="mb-4">
                    <p className="text-xs text-gray-500">
                      Select the merchant bank account(s) that should be available for this branch, then click “Save Selected”.
                    </p>
                  </div>

                  <table className="accounts-table">
                    <thead className="table-header">
                      <tr>
                        <th>Select</th>
                        <th>Bank</th>
                        <th>Branch</th>
                        <th>Account Number</th>
                        <th>Account Type</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {mainMerchantAccounts.map(acc => (
                        <tr key={acc.id}>
                          <td className="table-cell">
                            <input
                              type="checkbox"
                              checked={selectedMerchantAccountIds.includes(acc.id)}
                              onChange={() => toggleMerchantAccountSelection(acc.id)}
                            />
                          </td>
                          <td className="table-cell">{acc.bankName}</td>
                          <td className="table-cell">{acc.branchName}</td>
                          <td className="table-cell">•••• •••• {(acc.accountNumber || acc.accountNo || '').toString().slice(-4)}</td>
                          <td className="table-cell">{acc.accountType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-6">
                    <label className="form-label">Upload Account Proof (optional)</label>
                    <div className="file-upload-area">
                      <p className="upload-text">Attach proof documents once (applied to all selected accounts)</p>
                      <button className="btn-browse" onClick={() => reuseFileInputRef.current.click()}>Browse Files</button>
                      <input type="file" multiple className="hidden" ref={reuseFileInputRef} onChange={handleReuseFileSelect} />
                      {reuseSelectedFiles.length > 0 && (
                        <div className="mt-2 text-xs text-green-600">
                          {reuseSelectedFiles.length} file(s) selected
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end mt-4 flex-gap-2">
                    <button className="btn-secondary" onClick={() => setSelectedMerchantAccountIds([])}>Clear Selection</button>
                    <button className="btn-primary" onClick={handleSaveSelectedMerchantAccountsToBranch}>
                      Save Selected
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {useMerchantAccount === 'no' && (
            (!isBranchSpecific || (isBranchSpecific && selectedBranch)) ? (
        <div className="form-card">
          {/* <div className="p-3 mb-4 border border-blue-200 rounded bg-blue-50">
            <p className="text-xs font-semibold text-blue-800">
              Creating Account For: {isBranchSpecific 
                ? `Branch - ${availableBranches.find(b => b.uuid === selectedBranch)?.name || 'Unknown'}` 
                : 'Main Merchant'}
            </p>
            <p className="mt-1 text-xs text-blue-600">
              Branch UUID: {isBranchSpecific ? (selectedBranch || 'Not Selected') : 'N/A (Main Merchant Account)'}
            </p>
          </div> */}
          <h2 className="section-title">
            {isBranchSpecific 
              ? `Add Bank Account for ${availableBranches.find(b => b.uuid === selectedBranch)?.name || 'Branch'}` 
              : 'Add Bank Account for Main Merchant'}
          </h2>
          
          <div className="grid-cols-2">
            <div>
              <label className="form-label required">Bank</label>
              <select className="form-select" value={bankId} onChange={(e) => { setBankId(e.target.value); setBankBranchId(''); }}>
                <option value="">{metaLoading ? 'Loading...' : 'Select bank'}</option>
                {banks.map(bank => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label required">Branch</label>
              <select className="form-select" value={bankBranchId} onChange={(e) => setBankBranchId(e.target.value)} disabled={!bankId}>
                <option value="">{!bankId ? 'Select bank first' : 'Select branch'}</option>
                {filteredBankBranches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label required">Account Type</label>
              <select className="form-select" value={accountTypeId} onChange={(e) => setAccountTypeId(e.target.value)}>
                <option value="">{metaLoading ? 'Loading...' : 'Select account type'}</option>
                {accountTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label required">Account Number</label>
              <input type="text" placeholder="Enter account number" className="form-input" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
            </div>
            <div>
              <label className="form-label required">Account Holder Name</label>
              <input type="text" placeholder="Enter account holder name" className="form-input" value={holderName} onChange={(e) => setHolderName(e.target.value)} />
            </div>
          </div>
          
          <div className="mt-6">
            <label className="form-label required">Upload Account Proof</label>
            <div className="file-upload-area" onDrop={handleDrop} onDragOver={handleDragOver}>
              <p className="upload-text">Drag and drop files here</p>
              <p className="upload-or">or</p>
              <button className="btn-browse" onClick={() => fileInputRef.current.click()}>Browse Files</button>
              <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileSelect} />
              {selectedFiles.length > 0 && (
                <div className="mt-2 text-xs text-green-600">
                  {selectedFiles.length} file(s) selected
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 text-xs text-gray-500"><p>* Required fields</p></div>
          
          <div className="flex justify-end mt-4 flex-gap-2">
            <button className="btn-secondary">Cancel</button>
            <button className="btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      ) : (
        <div className="form-card">
          <div className="py-8 text-center">
            <p className="mb-2 text-sm text-gray-500">⚠ Branch not selected</p>
            <p className="text-xs text-gray-400">Please select a branch from the dropdown above to add branch-specific bank accounts.</p>
          </div>
        </div>
      )
    )}
      
      {isBranchSpecific && selectedBranch && (
        <div className="form-card">
          <h2 className="section-title">
            {availableBranches.find(b => b.uuid === selectedBranch)?.name || 'Branch'} - Bank Accounts
          </h2>
          
          {accountsLoading ? (
            <p className="py-8 text-xs text-center text-gray-500">Loading accounts...</p>
          ) : branchAccounts.length === 0 ? (
            <p className="py-8 text-xs text-center text-gray-500">No branch-specific bank accounts found for this branch.</p>
          ) : (
            <table className="accounts-table">
              <thead className="table-header">
                <tr>
                  <th>Bank</th>
                  <th>Branch</th>
                  <th>Account Number</th>
                  <th>Account Type</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody className="table-body">
                {branchAccounts.map(acc => (
                  <tr key={acc.id}>
                    <td className="table-cell">{acc.bankName}</td>
                    <td className="table-cell">{acc.branchName}</td>
                    <td className="table-cell">•••• •••• {acc.accountNumber.slice(-4)}</td>
                    <td className="table-cell">{acc.accountType}</td>
                    {/* <td className="table-actions">
                      <button className="action-btn action-btn-blue"><Eye size={16} /></button>
                      <button className="action-btn action-btn-yellow"><Edit size={16} /></button>
                      <button className="action-btn action-btn-red"><Trash2 size={16} /></button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          <div className="mt-4 note-card">
            <p className="text-xs">
              <span className="font-semibold">Note:</span> These are bank accounts specific to the selected branch. 
              The merchant also has common bank accounts available for use.
            </p>
          </div>
          
          <div className="flex justify-between mt-4">
            <button onClick={onBackToMerchant} className="btn-back">
              <span className="back-arrow">←</span> Back to Merchant
            </button>
            <div className="flex-gap-2">
              <button className="btn-secondary">Cancel</button>
              <button className="btn-primary">Save All</button>
            </div>
          </div>
        </div>
      )}

      {/* {isBranchSpecific && !selectedBranch && (
        <div className="form-card">
          <div className="py-12 text-center">
            <p className="mb-2 text-sm text-gray-500">⚠ No branch selected</p>
            <p className="text-xs text-gray-400">Please select a branch from the Account Association section above to manage branch-specific bank accounts.</p>
          </div>
        </div>
      )} */}
    </div>
  );
}